/**
 * Flat path diff between two analysis configuration snapshots (M2 contract 9, F6).
 *
 * Both inputs are the `config.snapshot` / `analysis_config_snapshot` objects sealed on a run
 * (`{ engines: { gestures: {...}, coherence: {...} }, ... }`). Nested objects are flattened to
 * dotted paths; arrays are compared as a whole (JSON) so a reordered gesture catalog reads as
 * one change instead of dozens. Pure: no Vue, no store.
 */

/** Human labels for the keys analysts recognise; unknown keys fall back to the raw path. */
export const KNOWN_CONFIG_LABELS = Object.freeze({
  'engines.gestures.gestureThresholdMs': 'Gesture threshold (ms)',
  'engines.gestures.aboveBaselineMultiplier': 'Above-baseline multiplier',
  'engines.gestures.belowBaselineMultiplier': 'Below-baseline multiplier',
  'engines.gestures.baselineWindowSeconds': 'Baseline window (s)',
  'engines.gestures.targetFps': 'Target FPS',
  'engines.gestures.sagemakerBatchSize': 'SageMaker batch size',
  'engines.gestures.sagemakerConcurrency': 'SageMaker concurrency',
  'engines.gestures.sampledFramesJpegQuality': 'Sampled frames JPEG quality',
  'engines.gestures.gestureCatalog': 'Gesture catalog',
  'engines.gestures.absoluteBands.rareMaxCount': 'Absolute band: rare max count',
  'engines.gestures.absoluteBands.intermediateMaxCount': 'Absolute band: intermediate max count',
  'engines.coherence.semanticWindowSeconds': 'Semantic window (s)',
  'engines.coherence.bulkSize': 'LLM bulk size',
  'engines.coherence.segmentStrategy': 'Segment strategy',
  'engines.coherence.reductionByRule': 'Reduction by rule',
  'engines.coherence.semaphoreThresholdGood': 'Semaphore threshold: good',
  'engines.coherence.semaphoreThresholdWarn': 'Semaphore threshold: warn',
  'engines.coherence.ideaCoincidenceThresholdPct': 'Idea coincidence threshold (%)',
  'engines.coherence.detectionThresholdPct': 'Detection threshold (%) (deprecated alias)',
  'engines.coherence.transcriptionProvider': 'Transcription provider',
  'engines.coherence.ruleset': 'Coherence ruleset',
  interview_analysis_config_id: 'Analysis config id',
  gestures_mongo_config_id: 'Gestures config document',
  version: 'Config version',
  engines_hash: 'Engines hash',
});

/** Paths whose subtree is compared as a single value (arrays of records, free-form maps). */
const ATOMIC_PATHS = new Set([
  'engines.gestures.gestureCatalog',
  'engines.coherence.reductionByRule',
  'engines.coherence.ruleset',
]);

function isPlainObject(value) {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

/**
 * Flattens an object to `{ 'a.b.c': value }`. Arrays and the ATOMIC_PATHS subtrees are kept
 * whole so they diff as one entry.
 */
export function flattenConfig(value, prefix = '', out = {}) {
  if (!isPlainObject(value)) {
    if (prefix) out[prefix] = value;
    return out;
  }
  Object.keys(value)
    .sort()
    .forEach((key) => {
      const path = prefix ? `${prefix}.${key}` : key;
      const child = value[key];
      if (isPlainObject(child) && !ATOMIC_PATHS.has(path)) {
        flattenConfig(child, path, out);
      } else {
        out[path] = child;
      }
    });
  return out;
}

function stable(value) {
  if (value === undefined) return 'undefined';
  if (isPlainObject(value)) {
    const sorted = {};
    Object.keys(value)
      .sort()
      .forEach((key) => {
        sorted[key] = value[key];
      });
    return JSON.stringify(sorted, (_key, v) => (isPlainObject(v) ? flattenConfig(v) : v));
  }
  return JSON.stringify(value);
}

export function configLabelFor(path) {
  return KNOWN_CONFIG_LABELS[path] || path;
}

/** Short, single-line rendering of a config value for the diff table. */
export function formatConfigValue(value) {
  if (value === undefined) return '—';
  if (value === null) return 'null';
  if (Array.isArray(value)) return `${value.length} item${value.length === 1 ? '' : 's'}`;
  if (isPlainObject(value)) {
    const keys = Object.keys(value);
    return keys.length ? `${keys.length} key${keys.length === 1 ? '' : 's'}` : '{}';
  }
  return String(value);
}

/**
 * @param {object|null} previous snapshot of the previous run (null -> everything is "added")
 * @param {object|null} current snapshot of the run being viewed
 * @returns {Array<{ path: string, label: string, kind: 'added'|'removed'|'changed',
 *   before: unknown, after: unknown }>} sorted by path; empty when identical
 */
export function diffConfigSnapshots(previous, current) {
  const before = flattenConfig(previous || {});
  const after = flattenConfig(current || {});
  const paths = new Set([...Object.keys(before), ...Object.keys(after)]);
  const changes = [];
  [...paths].sort().forEach((path) => {
    const hasBefore = Object.prototype.hasOwnProperty.call(before, path);
    const hasAfter = Object.prototype.hasOwnProperty.call(after, path);
    if (hasBefore && hasAfter && stable(before[path]) === stable(after[path])) return;
    changes.push({
      path,
      label: configLabelFor(path),
      kind: !hasBefore ? 'added' : !hasAfter ? 'removed' : 'changed',
      before: hasBefore ? before[path] : undefined,
      after: hasAfter ? after[path] : undefined,
    });
  });
  return changes;
}

/** Snapshot of a run from either shape (metadata.run has no snapshot; gestures doc does). */
export function snapshotOf(run, gestures = null) {
  return (
    run?.config?.snapshot ||
    gestures?.analysis_config_snapshot ||
    gestures?.analysisConfigSnapshot ||
    null
  );
}
